import type { Ring, Vec2, Vec3 } from '../../../../geo/types';
import { transformNormal, transformPoint, type Frame, type MeshBuilder, type VertexStyle } from '../../../mesh/meshBuilder';
import { ensureCCW } from '../../../../geo/polygon';
import { orientedFace, triangulateRing } from '../../solids';
import { panelPoint, pointedArchRise, type Panel } from '../../panels';
import { panelCoordsOf, wallFace } from '../wallCoords';
import type { TracerySink } from './tracerySink';

/**
 * Church windows glazed in their stone: the leaded glass stands in the tracery a little behind the
 * wall face, the jambs and arch lined with stone (the Oude Kerk's 'dagkanten' are natural stone blocks
 * in one piece with the side mullions; Janse 2004, section 1.9). How deep is not recorded; the walls
 * above the aisle roofs are 1½ bricks, about 35 cm (section 1.6), so the glass stands 25 cm in. Iron
 * saddle bars cross the lights every 90 cm (section 1.9), one at each joint of the leaded panels.
 */
export const GLAZING = { depth: 0.25, saddleBarsEvery: 0.9 } as const;

export interface Glazing {
  /** The stone of the jambs and arches. */
  readonly reveal: VertexStyle;
  /** Where traceried windows go; without it the windows keep their reveals and stay plain. */
  readonly tracery?: { readonly sink: TracerySink; readonly edgeId: number };
  /** The leaded glass of the pointed windows, patterned (STYLE §15, leadedGlass); a doorway's stays plain. */
  readonly leaded?: VertexStyle;
}

/** The same styles with the glazing's reveals kept and its tracery and leading left out, for openings whose glazing is not recorded. */
export function withoutTracery<S extends { readonly glazing?: Glazing }>(styles: S): S {
  return styles.glazing ? { ...styles, glazing: { reveal: styles.glazing.reveal } } : styles;
}

export interface GlazedStyles {
  readonly wall: VertexStyle;
  readonly opening: VertexStyle;
  readonly glazing: Glazing;
}

function normalOf(panel: Panel): Vec3 {
  const [u, v] = [panel.uAxis, panel.vAxis];
  return [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]];
}

function sunk(panel: Panel, depth: number): Panel {
  const n = normalOf(panel);
  const o = panel.origin;
  return { ...panel, origin: [o[0] - n[0] * depth, o[1] - n[1] * depth, o[2] - n[2] * depth] };
}

/**
 * The window a pointed opening of `segments` to each arc makes (panels.pointedWindow): its jambs, sill
 * and springing, or undefined for any other opening.
 */
export function pointedOpeningOf(ring: Ring, segments: number): { x0: number; x1: number; sill: number; spring: number } | undefined {
  if (ring.length !== 2 * segments + 3) return undefined;
  const [a, b, c] = [ring[0]!, ring[1]!, ring[2]!];
  const apex = ring[segments + 2]!;
  const last = ring[ring.length - 1]!;
  const width = b[0] - a[0];
  const square = Math.abs(a[1] - b[1]) < 1e-9 && Math.abs(c[0] - b[0]) < 1e-9 && Math.abs(last[0] - a[0]) < 1e-9 && Math.abs(last[1] - c[1]) < 1e-9;
  const pointed = Math.abs(apex[0] - (a[0] + b[0]) / 2) < 1e-9 && Math.abs(apex[1] - c[1] - pointedArchRise(width)) < 1e-9;
  return width > 0 && square && pointed ? { x0: a[0], x1: b[0], sill: a[1], spring: c[1] } : undefined;
}

/* Stone lining each edge of the opening from the face back to the glass, facing into the opening. */
function reveals(builder: MeshBuilder, style: VertexStyle, frame: Frame, panel: Panel, hole: Ring, depth: number): void {
  const back = sunk(panel, depth);
  const centre = hole.reduce((s, p): Vec2 => [s[0] + p[0] / hole.length, s[1] + p[1] / hole.length], [0, 0]);
  hole.forEach((p, i) => {
    const q = hole[(i + 1) % hole.length]!;
    const quad = [panelPoint(panel, p[0], p[1]), panelPoint(panel, q[0], q[1]), panelPoint(back, q[0], q[1]), panelPoint(back, p[0], p[1])];
    const mid = panelPoint(panel, (p[0] + q[0]) / 2, (p[1] + q[1]) / 2);
    const into = panelPoint(panel, centre[0], centre[1]);
    orientedFace(builder, style, frame, quad, [into[0] - mid[0], into[1] - mid[1], into[2] - mid[2]]);
  });
}

/* A pointed window above the wall's foot; a doorway opens from the foot. */
function windowOf(hole: Ring, segments: number): ReturnType<typeof pointedOpeningOf> {
  const window = pointedOpeningOf(hole, segments);
  return window && window.sill > 0 ? window : undefined;
}

/* The glass on the sunk panel: leaded from the light's lower-left corner in a window, else plain. */
function glass(builder: MeshBuilder, styles: GlazedStyles, frame: Frame, panel: Panel, hole: Ring, segments: number): void {
  const back = sunk(panel, GLAZING.depth);
  const points = hole.map((p) => panelPoint(back, p[0], p[1]));
  const local = panelCoordsOf(back);
  const window = styles.glazing.leaded && windowOf(hole, segments);
  if (!styles.glazing.leaded || !window) {
    builder.polygon(styles.opening, frame, points, local);
    return;
  }
  const leaded = { ...styles.glazing.leaded, extent: [window.x1 - window.x0, GLAZING.saddleBarsEvery] as const };
  builder.polygon(leaded, frame, points, (p) => {
    const [a, b] = local(p);
    return [a - window.x0, b - window.sill];
  });
}

/* A doorway takes no tracery. */
function register(styles: GlazedStyles, frame: Frame, panel: Panel, hole: Ring, segments: number): void {
  const tracery = styles.glazing.tracery;
  const window = tracery && windowOf(hole, segments);
  if (!tracery || !window) return;
  const foot = panelPoint(sunk(panel, GLAZING.depth), window.x0, window.sill);
  tracery.sink.add(
    {
      origin: transformPoint(foot, frame),
      right: transformNormal(panel.uAxis, frame),
      outward: transformNormal(normalOf(panel), frame),
      width: window.x1 - window.x0,
      rise: window.spring - window.sill,
    },
    tracery.edgeId
  );
}

/**
 * A wall face with its openings cut through it: each lined with stone reveals back to its glass,
 * `GLAZING.depth` behind the face, and handed to the tracery sink if it is a pointed window.
 */
export function glazedWall(builder: MeshBuilder, styles: GlazedStyles, frame: Frame, panel: Panel, outline: Ring, openings: readonly Ring[], segments: number): void {
  const holes = openings.map(ensureCCW);
  const face = wallFace(styles.wall, frame, panel, outline);
  for (const tri of triangulateRing(ensureCCW(outline), holes)) {
    builder.polygon(face.style, frame, tri.map((p) => panelPoint(panel, p[0], p[1])), face.uv);
  }
  for (const hole of holes) {
    reveals(builder, styles.glazing.reveal, frame, panel, hole, GLAZING.depth);
    glass(builder, styles, frame, panel, hole, segments);
    register(styles, frame, panel, hole, segments);
  }
}
